import { useState, useCallback, useRef } from 'react';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { PListDetailsDto, EmptyPallet, TaValidationIssue } from '../../../../../types/picking';
import { logger } from '../../../../../services/loggingService';
import { pickingService } from '../../../../../services/pickingService';
import { plistOrderService, PListOrderDto } from '../../../../../services/plistOrderService';
import { shipmentLineService } from '../../../../../services/shipmentLineService';
import { packingService } from '../../../../../services/packingService';
import { printingService } from '../../../../../services/printingService';
import { settingsService } from '../../../../../services/settingsService';
import { deliveryMethodService } from '../../../../../services/deliveryMethodService';
import { PickCompletionWarning } from '../modals/PickCompletionWarningModal';
import { SSCCLabelType } from '../../../../../types/printing';
import { TransitPackagingDto } from '../../../../../types/shipmentLine';
import { CarrierDistributionResult } from '../modals/CarrierDistributionModal';
import { ShipmentLineRow } from '../../../../../types/shipmentLine';
import { UserProfileDto } from '../../../../../types/userProfile';
import { ExtendedPickingLine } from './usePickingData';

export interface UsePickingCompletionReturn {
  // Modal states
  showDeliveryInfoModal: boolean;
  setShowDeliveryInfoModal: React.Dispatch<React.SetStateAction<boolean>>;
  showPackZoneSelection: boolean;
  setShowPackZoneSelection: React.Dispatch<React.SetStateAction<boolean>>;
  showQuantityDeviation: boolean;
  quantityDeviations: Array<{
    pListLineNo: number;
    itemNo: string;
    itemDescription: string;
    qtyOrdered: number;
    qtyPicked: number;
    deviation: number;
  }>;
  showSignatureModal: boolean;
  setShowSignatureModal: React.Dispatch<React.SetStateAction<boolean>>;

  // Leveringsmåte-varsel ved avslutt plukk (Strømmes)
  showPickCompletionWarning: boolean;
  pickCompletionWarnings: PickCompletionWarning[];
  handlePickCompletionWarningConfirm: () => void;
  handlePickCompletionWarningCancel: () => void;

  // TA-forhåndsvalidering før fullføring (36369121)
  showTaValidation: boolean;
  taValidationIssues: TaValidationIssue[];
  taValidationBusy: boolean;
  handleTaValidationCancel: () => void;
  handleTaValidationProceed: () => Promise<void>;

  // Lastbærer-gate + fordelingsbilde (36334239)
  showCarrierGate: boolean;
  showCarrierDistribution: boolean;
  carrierBusy: boolean;
  /** React-key for DeliveryInfoModal — endres når Transport-detaljer må lastes på nytt */
  deliveryInfoKey: number;
  handleCompleteWithoutCarrier: () => Promise<void>;
  handleOpenCarrierDistribution: () => void;
  handleCarrierDistributionConfirm: (result: CarrierDistributionResult[]) => Promise<void>;
  handleCarrierGateCancel: () => void;
  handleCarrierDistributionCancel: () => void;

  // Handlers
  handleCompletePicking: () => Promise<void>;
  handleSendToPacking: () => Promise<void>;
  handlePackZoneConfirm: (packZoneID: string) => Promise<void>;
  handleDeliveryInfoConfirm: (
    pListData: DeliveryInfoPListData,
    pListOrdersData: Map<number, DeliveryInfoPListData>,
    shipmentLinesData?: Map<number, ShipmentLineRow[]>,
    consolidate?: boolean
  ) => Promise<void>;
  handleQuantityDeviationConfirm: () => void;
  handleQuantityDeviationCancel: () => void;
  handleSignatureConfirm: (signatureDataUrl: string) => Promise<void>;
}

export interface DeliveryInfoPListData {
  totalVolume: string;
  totalWeight: string;
  numOfPackages: string;
  numOfPallets: string;
  carrierName: string;
  deliveryMethodNo: number | null;
  packZoneID: string;
  packageRemark: string;
}

interface UsePickingCompletionDeps {
  pListId: number;
  pickingDetails: PListDetailsDto | null;
  pickingLines: ExtendedPickingLine[];
  pListOrders: PListOrderDto[];
  setPListOrders: React.Dispatch<React.SetStateAction<PListOrderDto[]>>;
  emptyPallets: EmptyPallet[];
  loadPickingDetails: (profile?: any) => Promise<void>;
  onBack: () => void;
  /** Lastbærer-typene (36334239): brukes til tara-vekt og mål på kollilinjene */
  carriers?: TransitPackagingDto[];
  /** Oppfrisker pall-listen etter at fordelingen har opprettet nye paller */
  loadPallets?: () => Promise<void> | void;
}

export function usePickingCompletion(deps: UsePickingCompletionDeps): UsePickingCompletionReturn {
  const { t } = useTranslation();
  const {
    pListId,
    pickingDetails,
    pickingLines,
    pListOrders,
    setPListOrders,
    emptyPallets,
    loadPickingDetails,
    onBack,
    carriers = [],
    loadPallets,
  } = deps;

  // Modal states
  const [showDeliveryInfoModal, setShowDeliveryInfoModal] = useState(false);
  // TA-forhåndsvalidering (36369121): vises når nShift-valideringen fant avvik
  const [showTaValidation, setShowTaValidation] = useState(false);
  const [taValidationIssues, setTaValidationIssues] = useState<TaValidationIssue[]>([]);
  const [taValidationBusy, setTaValidationBusy] = useState(false);
  // Lastbærer-gate (36334239): vises når backend avviser med CARRIER_REQUIRED
  const [showCarrierGate, setShowCarrierGate] = useState(false);
  const [showCarrierDistribution, setShowCarrierDistribution] = useState(false);
  const [carrierBusy, setCarrierBusy] = useState(false);
  // Økes når kollilinjer er registrert utenfra: remonterer DeliveryInfoModal så den
  // laster de nye Transport-detaljene i stedet for å vise sin gamle (tomme) state.
  const [deliveryInfoKey, setDeliveryInfoKey] = useState(0);
  const [showPackZoneSelection, setShowPackZoneSelection] = useState(false);
  const [showQuantityDeviation, setShowQuantityDeviation] = useState(false);
  const [quantityDeviations, setQuantityDeviations] = useState<Array<{
    pListLineNo: number;
    itemNo: string;
    itemDescription: string;
    qtyOrdered: number;
    qtyPicked: number;
    deviation: number;
  }>>([]);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  // Leveringsmåte-varsel ved avslutt plukk (Strømmes): popup som operatøren må
  // bekrefte med OK når minst én ordre har leveringsmåte med varslingsflagg
  // (f.eks. ekspress-sending som ikke må blandes med vanlig post).
  const [showPickCompletionWarning, setShowPickCompletionWarning] = useState(false);
  const [pickCompletionWarnings, setPickCompletionWarnings] = useState<PickCompletionWarning[]>([]);

  // ─────────────────────────────────────────────────
  // Internal helpers
  // ─────────────────────────────────────────────────

  const checkEmptyPallets = useCallback((): boolean => {
    if (emptyPallets.length > 0) {
      const confirmDelete = window.confirm(
        `Du har ${emptyPallets.length} tomme paller som ikke er brukt.\n\n` +
        `Tomme paller lagres ikke i systemet og vil forsvinne når du fullfører plukking.\n\n` +
        `Vil du slette disse og fortsette?\n\n` +
        `Velg "OK" for å slette tomme paller og fullføre.\n` +
        `Velg "Avbryt" for å gå tilbake.`
      );

      if (!confirmDelete) {
        return false;
      }
      console.log(`Brukeren bekreftet sletting av ${emptyPallets.length} tomme paller`);
    }
    return true;
  }, [emptyPallets]);

  // Auto-print av innholdsetiketter ved avslutning (36334106): når settingen er på,
  // skrives innholdsetikett (KUN den, ikke SSCC-etikett) for alle lagrede paller.
  // Kjøres ETTER vellykket fullføring og blokkerer aldri — feil gir én samlet melding,
  // og per-pall-knappene i pallpanelet består som manuell reprint.
  const autoPrintContentLabels = useCallback(async () => {
    try {
      const setting = await settingsService.getEffectiveSetting('picking.autoPrintContentLabelsOnComplete');
      if ((setting?.currentValue || '').toString().toLowerCase() !== 'true') return;

      const companyNo = pickingDetails?.header.companyNo;
      if (!companyNo) return;

      const pallets = (await pickingService.getPallets(pListId)).filter(p => (p.totalItems ?? 0) > 0);
      if (pallets.length === 0) return;

      let failed = 0;
      for (const pallet of pallets) {
        try {
          // Innholdsetiketten bruker alltid innebygd mal (samme som pallpanelets knapp).
          const result = await printingService.printSSCCLabel(companyNo, {
            sscc: pallet.sscc,
            pListId,
            quantity: pallet.totalQuantity || undefined,
            labelType: SSCCLabelType.StandardPallet,
            includeContent: true,
            gtin: pallet.items?.[0]?.gtin || undefined,
            batchNo: pallet.items?.[0]?.itemBatchNo || undefined,
            expiryDate: pallet.items?.[0]?.itemBatchExpDate || undefined
          });
          if (!result.success) failed++;
        } catch {
          failed++;
        }
      }

      if (failed > 0) {
        alert(t('warehouse.modules.picking.alerts.contentLabelsAutoPrintFailed', { 0: String(failed) },
          '{0} innholdsetikett(er) kunne ikke skrives ut. Plukket er fullført — bruk «Innholdsetikett» i pallpanelet for å skrive ut på nytt.'));
      }
    } catch (err) {
      // Aldri blokkér fullføringen på utskriftsproblemer.
      console.warn('Auto-print av innholdsetiketter feilet:', err);
    }
  }, [pListId, pickingDetails, t]);

  /** Felles avslutning: etiketter, lukk alle dialoger, tilbake til lista. */
  const finishCompletion = useCallback(async () => {
    await autoPrintContentLabels();
    setShowDeliveryInfoModal(false);
    setShowCarrierGate(false);
    setShowCarrierDistribution(false);
    setShowTaValidation(false);
    onBack();
  }, [autoPrintContentLabels, onBack]);

  /**
   * Halen av fullføringen (complete + finish) — delt mellom normal flyt og
   * «Send likevel»-valget i TA-valideringsdialogen (36369121), slik at
   * lastbærer-gaten (CARRIER_REQUIRED) virker likt i begge veier.
   */
  const completePickingAndFinish = useCallback(async () => {
    try {
      await pickingService.completePicking(pListId);
    } catch (completeError: any) {
      if (completeError?.errorCode === 'CARRIER_REQUIRED') {
        setShowCarrierGate(true);
        return; // flyten fortsetter i gate-handlerne
      }
      throw completeError;
    }
    await finishCompletion();
  }, [pListId, finishCompletion]);

  // ── TA-forhåndsvalidering (36369121) ──────────────────────────────────

  /** «Avbryt og rett» — tilbake til leveranseinformasjonen, plukket forblir åpent. */
  const handleTaValidationCancel = useCallback(() => {
    setShowTaValidation(false);
  }, []);

  /** «Send likevel, og rett i nShift» — fullfør; overstyringen logges sporbart. */
  const handleTaValidationProceed = useCallback(async () => {
    setTaValidationBusy(true);
    try {
      // Sporbarhet: hvem overstyrte hvilke avvik — uten dette er det umulig i ettertid
      // å skille dårlige data fra en bevisst beslutning.
      logger.logUserAction('ta_validation_override', {
        pListId,
        issues: taValidationIssues.map(i => `${i.orderNo}: ${i.message}`)
      });
      setShowTaValidation(false);
      await completePickingAndFinish();
    } catch (error) {
      console.error('Feil ved fullføring etter TA-overstyring:', error);
      alert(t('warehouse.modules.picking.errors.completeFailed', {}, 'Kunne ikke avslutte plukket. Prøv igjen.'));
    } finally {
      setTaValidationBusy(false);
    }
  }, [pListId, taValidationIssues, completePickingAndFinish, t]);

  // ── Lastbærer-gate (36334239) ──────────────────────────────────

  /** «Avslutt uten lastbærer» — bekreftelsen stemples sporbart på PList server-side. */
  const handleCompleteWithoutCarrier = useCallback(async () => {
    setCarrierBusy(true);
    try {
      await pickingService.completePicking(pListId, undefined, true);
      await finishCompletion();
    } catch (err) {
      console.error('Kunne ikke avslutte uten lastbærer:', err);
      alert(t('warehouse.modules.picking.errors.completeFailed', {}, 'Kunne ikke avslutte plukket. Prøv igjen.'));
    } finally {
      setCarrierBusy(false);
    }
  }, [pListId, finishCompletion, t]);

  const handleOpenCarrierDistribution = useCallback(() => {
    setShowCarrierGate(false);
    setShowCarrierDistribution(true);
  }, []);

  const handleCarrierGateCancel = useCallback(() => {
    // Operatøren går tilbake til leveranseinformasjonen — plukket forblir åpent
    setShowCarrierGate(false);
  }, []);

  const handleCarrierDistributionCancel = useCallback(() => {
    setShowCarrierDistribution(false);
    setShowCarrierGate(true); // tilbake til valget
  }, []);

  /**
   * Persisterer fordelingen: hver lastbærer blir en ekte pall (OrderSSCC-rader, én per
   * linje) og deretter en kollilinje i Transport-detaljer — nøyaktig samme datavei som
   * paller registrert på forhånd i pallpanelet. Backend beregner pallens bruttovekt fra
   * varevekt; tara fra lastbæreren legges til på kollilinjen (samme regel som
   * auto-kollilinjene i DeliveryInfoModal).
   */
  const handleCarrierDistributionConfirm = useCallback(async (result: CarrierDistributionResult[]) => {
    if (result.length === 0) return;
    setCarrierBusy(true);
    try {
      // 1) Opprett pallene
      const created: Array<{ sscc: string; orderNo: number; transitPackagingID: number }> = [];
      for (const carrier of result) {
        let sscc: string | null = null;
        try {
          sscc = await pickingService.generateSscc();
        } catch {
          sscc = null; // SSCC ikke konfigurert for firmaet — fall tilbake til lokal nøkkel
        }
        const palletKey = sscc || `PALL-${Date.now()}-${created.length + 1}`;

        for (const line of carrier.lines) {
          const pickLine = pickingLines.find(l => l.pListLineNo === line.pListLineNo);
          await pickingService.createOrUpdateSSCC(pListId, {
            companyNo: '', // settes av backend fra TenantContext
            pListID: pListId,
            orderNo: line.orderNo,
            // pListLineNo som lineNo — samme konvensjon som handleUpdateQuantity og split
            lineNo: line.pListLineNo,
            sscc: palletKey,
            quantity: line.qty,
            ssccLevel: 1,
            itemBatchNo: '',
            itemBatchExpDate: null,
            stackingWeight: 0,
            grossWeight: 0, // backend beregner: Quantity x OrderLine.Weight
            locationName: pickLine?.binCode || pickLine?.location,
            transitPackagingID: carrier.transitPackagingID
          });
        }
        created.push({ sscc: palletKey, orderNo: carrier.lines[0]?.orderNo ?? 0, transitPackagingID: carrier.transitPackagingID });
      }

      // 2) Bygg kollilinjer fra de lagrede pallene (får backend-beregnet bruttovekt)
      const pallets = await pickingService.getPallets(pListId);
      const byOrder = new Map<number, typeof created>();
      created.forEach(c => byOrder.set(c.orderNo, [...(byOrder.get(c.orderNo) ?? []), c]));

      for (const [orderNo, palletsForOrder] of Array.from(byOrder.entries())) {
        // Eksisterende kollilinjer MÅ sendes med — save-all sletter linjer som ikke er i settet
        let existing: Array<any> = [];
        try {
          existing = await shipmentLineService.getByPListOrder(pListId, orderNo);
        } catch {
          existing = [];
        }

        const newLines = palletsForOrder.map((c, i) => {
          const summary = pallets.find(p => p.sscc === c.sscc);
          const type = carriers.find(tp => tp.transitPackagingID === c.transitPackagingID);
          const gross = (summary?.totalWeight || 0) + (type?.weight || 0);
          return {
            transitPackagingID: c.transitPackagingID,
            description1: c.sscc,
            numOfPackages: 1,
            weight: gross > 0 ? Math.round(gross * 100) / 100 : null,
            volume: null,
            height: null, // pallhøyde varierer — operatøren fyller inn ved behov
            width: type?.width ?? null,
            length: type?.length ?? null,
            sorting: existing.length + i
          };
        });

        await shipmentLineService.saveAll({
          pListID: pListId,
          orderNo,
          lines: [
            ...existing.map((l, i) => ({
              lineID: l.lineID || undefined,
              transitPackagingID: l.transitPackagingID,
              description1: l.description1,
              numOfPackages: l.numOfPackages,
              weight: l.weight,
              volume: l.volume,
              height: l.height,
              width: l.width,
              length: l.length,
              sorting: l.sorting ?? i
            })),
            ...newLines
          ]
        });
      }

      // 3) TILBAKE til leveranseinformasjonen med kollilinjene utfylt — operatøren skal
      //    se og kunne justere Transport-detaljer (vekt, mål, høyde) FØR fullføringen.
      //    Plukket fullføres først når hen bekrefter der; da finnes det kollilinjer med
      //    lastbærer, så gaten slipper gjennom uten å spørre på nytt.
      try {
        await loadPallets?.();
      } catch {
        // pall-refresh er kosmetikk — skal ikke stoppe flyten
      }
      setShowCarrierDistribution(false);
      // Remonterer DeliveryInfoModal slik at den laster de nye kollilinjene
      setDeliveryInfoKey(k => k + 1);
    } catch (err) {
      console.error('Kunne ikke registrere lastbærere:', err);
      alert(t('warehouse.modules.picking.errors.carrierDistributionFailed', {},
        'Kunne ikke registrere lastbærerne. Ingen endring er gjort på plukket — prøv igjen.'));
    } finally {
      setCarrierBusy(false);
    }
  }, [pListId, carriers, pickingLines, loadPallets, t]);

  const validateSSCC = useCallback(async (action: string): Promise<boolean> => {
    try {
      const ssccValidation = await pickingService.validateSSCCForCompletion(pListId);
      if (!ssccValidation.isValid) {
        alert(
          `Kan ikke ${action}.\n\n` +
          `${ssccValidation.message}\n\n` +
          `Skriv ut SSCC-etiketter for disse ordrene før du ${action === 'fullføre plukklisten' ? 'fullfører' : 'sender til pakking'}.`
        );
        return false;
      }
    } catch (err) {
      // Fail-open: if validation endpoint fails, allow completion to proceed
      console.error('Error validating SSCC (continuing):', err);
    }
    return true;
  }, [pListId]);

  const getDeviationLines = useCallback(() => {
    return pickingLines
      // Manko-linjer ([X]) håndteres av egen manko-advarsel, ikke som ordinært avvik (Ariens).
      .filter(line => line.qtyPicked !== line.qty && line.binCode !== '[X]')
      .map(line => ({
        pListLineNo: line.pListLineNo,
        itemNo: line.itemNo || '',
        itemDescription: line.itemDescription || 'Ukjent vare',
        qtyOrdered: line.qty,
        qtyPicked: line.qtyPicked,
        deviation: line.qtyPicked - line.qty
      }));
  }, [pickingLines]);

  // Manko-linjer ([X]): kan ikke plukkes (regelmotor fant ingen saldo, eller operatør meldte tomt).
  const getShortageLines = useCallback(() => {
    return pickingLines.filter(line => line.binCode === '[X]');
  }, [pickingLines]);

  // Advarsel-men-tillat (Ariens): hvis det finnes gjenstående manko, be operatøren bekrefte
  // før plukket avsluttes/sendes til pakking. Returnerer false hvis operatøren avbryter.
  const confirmShortagesIfAny = useCallback((action: string) => {
    const shortage = getShortageLines();
    if (shortage.length === 0) return true;
    return window.confirm(
      `${shortage.length} linje(r) har manko (ingen saldo / lokasjon [X]) og kan ikke plukkes.\n\n` +
      `Vil du ${action} likevel?\n\n` +
      `Velg "OK" for å fortsette.\n` +
      `Velg "Avbryt" for å gå tilbake og løse manko.`
    );
  }, [getShortageLines]);

  const proceedToDeliveryInfo = useCallback(async () => {
    // Reload PListOrders to ensure we have the latest data before opening modal
    try {
      const companyNo = pickingDetails!.header.companyNo;
      console.log('Reloading PListOrders before opening modal...');
      const orders = await plistOrderService.getByPListId(companyNo, pListId);
      console.log('Reloaded PListOrders:', orders);
      setPListOrders(orders);
    } catch (error) {
      console.error('Error reloading PListOrders:', error);
    }

    setShowDeliveryInfoModal(true);
  }, [pListId, pickingDetails, setPListOrders]);

  // ─────────────────────────────────────────────────
  // Public handlers
  // ─────────────────────────────────────────────────

  const handleCompletePicking = useCallback(async () => {
    // P14 brødre-plukk: et plukk som er delt på flere plukklister (aktive søsken) kan IKKE avsluttes
    // direkte — operatøren må sende det til pakking slik at ordren samles der. Backend håndhever også
    // dette (hard blokk), men vi varsler her for en tydelig operatøropplevelse.
    if (pickingDetails?.hasSiblings) {
      window.alert(
        'Dette plukket er delt på flere plukklister (brødre) og kan ikke avsluttes direkte.\n\n' +
        'Bruk «Til pakking» for å sende det videre, slik at hele ordren samles på pakking.'
      );
      return;
    }

    if (!checkEmptyPallets()) return;
    if (!(await validateSSCC('fullføre plukklisten'))) return;
    if (!confirmShortagesIfAny('fullføre')) return;

    const deviationLines = getDeviationLines();

    if (deviationLines.length > 0) {
      setQuantityDeviations(deviationLines);
      setShowQuantityDeviation(true);
      return;
    }

    await proceedToDeliveryInfo();
  }, [pickingDetails, checkEmptyPallets, validateSSCC, confirmShortagesIfAny, getDeviationLines, proceedToDeliveryInfo]);

  const handleSendToPacking = useCallback(async () => {
    // Validate packing location is configured and exists
    try {
      const packingValidation = await pickingService.validatePackingLocation();
      if (!packingValidation.isValid) {
        alert(packingValidation.errorMessage || 'Pakkelokasjon er ikke konfigurert.');
        return;
      }
    } catch (err) {
      console.error('Error validating packing location:', err);
      alert('Kunne ikke validere pakkelokasjon. Sjekk Systeminnstillinger.');
      return;
    }

    // Check for unused empty pallets
    if (emptyPallets.length > 0) {
      const confirmDelete = window.confirm(
        `Du har ${emptyPallets.length} tomme paller som ikke er brukt.\n\n` +
        `Tomme paller lagres ikke i systemet og vil forsvinne når du sender til pakking.\n\n` +
        `Vil du slette disse og fortsette?\n\n` +
        `Velg "OK" for å slette tomme paller og fortsette.\n` +
        `Velg "Avbryt" for å gå tilbake.`
      );

      if (!confirmDelete) {
        return;
      }
      console.log(`Brukeren bekreftet sletting av ${emptyPallets.length} tomme paller`);
    }

    if (!(await validateSSCC('sende til pakking'))) return;
    if (!confirmShortagesIfAny('sende til pakking')) return;

    // Check for quantity deviations (but allow them for "Til Pakking")
    const deviationLines = getDeviationLines();

    if (deviationLines.length > 0) {
      const deviationSummary = deviationLines.map(line =>
        `${line.itemDescription}: ${line.qtyPicked} av ${line.qtyOrdered}`
      ).join('\n');

      const confirmed = window.confirm(
        `Det er avvik på ${deviationLines.length} linje(r):\n\n${deviationSummary}\n\n` +
        `Vil du sende til pakking med disse avvikene?\n\n` +
        `Velg "OK" for å fortsette.\n` +
        `Velg "Avbryt" for å gå tilbake.`
      );

      if (!confirmed) {
        return;
      }
    }

    // Reload PListOrders and open pack zone selection modal
    try {
      const companyNo = pickingDetails!.header.companyNo;
      const orders = await plistOrderService.getByPListId(companyNo, pListId);
      setPListOrders(orders);
    } catch (err) {
      console.error('Error reloading PListOrders:', err);
    }
    setShowPackZoneSelection(true);
  }, [emptyPallets, validateSSCC, confirmShortagesIfAny, getDeviationLines, pListId, pickingDetails, setPListOrders]);

  const handlePackZoneConfirm = useCallback(async (packZoneID: string) => {
    try {
      const companyNo = pickingDetails!.header.companyNo;

      // Update PackZoneID on all PListOrders
      for (const order of pListOrders) {
        await plistOrderService.update(companyNo, pListId, order.orderNo, {
          packZoneID: packZoneID
        });
      }

      // Send to packing
      const result = await pickingService.sendToPacking(pListId);

      // Informasjonslapp på godset (36369120): én etikett per ordre, skrevet ut i det
      // plukket sendes til pakking. Gated bak picking.packZoneLabelReportId (av som
      // standard → no-op for alle andre kunder). Kalles ETTER vellykket sendToPacking
      // og er aldri blokkerende — men operatøren står her og skal få vite det med én
      // gang hvis lappen ikke kom ut.
      let labelWarning = '';
      try {
        const labelResult = await pickingService.printPackZoneLabels(pListId, packZoneID);
        if (labelResult.enabled && labelResult.labelsFailed > 0) {
          labelWarning = '\n\n' + t(
            'warehouse.modules.picking.packZoneLabel.printFailed',
            { '0': String(labelResult.labelsFailed) },
            'OBS: {0} pakkesone-etikett(er) kunne ikke skrives ut. Bruk reprint fra plukklisten.'
          );
        }
      } catch (labelError) {
        console.warn('Pakkesone-etikett feilet (send til pakking er gjennomført):', labelError);
        labelWarning = '\n\n' + t(
          'warehouse.modules.picking.packZoneLabel.printFailedGeneric',
          {},
          'OBS: pakkesone-etiketten kunne ikke skrives ut. Bruk reprint fra plukklisten.'
        );
      }

      setShowPackZoneSelection(false);
      // P14 brødre-plukk: sistemann får ekstra beskjed.
      if (result.isLastSibling) {
        alert(
          'Plukklisten er sendt til pakking!\n\n' +
          'Du er SISTEMANN — alle plukk i denne ordre-gruppen (brødre) er nå sendt til pakking.' +
          labelWarning
        );
      } else {
        alert('Plukklisten er sendt til pakking!' + labelWarning);
      }
      onBack();
    } catch (error) {
      console.error('Error sending to packing:', error);
      throw error;
    }
  }, [pListId, pickingDetails, pListOrders, onBack, t]);

  /** Selve fullføringen — kjøres etter at ev. leveringsmåte-varsel er bekreftet. */
  const executeDeliveryInfoConfirm = useCallback(async (
    pListData: DeliveryInfoPListData,
    pListOrdersData: Map<number, DeliveryInfoPListData>,
    shipmentLinesData?: Map<number, ShipmentLineRow[]>,
    consolidate?: boolean
  ) => {
    try {
      // Update PList with delivery info
      if (pickingDetails) {
        const updatedPList = {
          ID: pickingDetails.header.id,
          CompanyNo: pickingDetails.header.companyNo,
          PListID: pickingDetails.header.pListID,
          Description1: pickingDetails.header.description1,
          Description2: pickingDetails.header.description2,
          PListDate: pickingDetails.header.pListDate,
          NumOfOrderLines: pickingDetails.header.numOfOrderLines,
          Weight: pickingDetails.header.weight,
          Priority: pickingDetails.header.priority,
          PListStatus: pickingDetails.header.pListStatus,
          OperatorID: pickingDetails.header.operatorID,
          LockedBy: pickingDetails.header.lockedBy,
          StartWorkDateTime: pickingDetails.header.startWorkDateTime,
          StopWorkDateTime: pickingDetails.header.stopWorkDateTime,
          DeliveryDate: pickingDetails.header.deliveryDate,
          TotalVolume: pListData.totalVolume ? parseFloat(pListData.totalVolume) : null,
          TotalWeight: pListData.totalWeight ? parseFloat(pListData.totalWeight) : null,
          NumOfPackages: pListData.numOfPackages ? parseInt(pListData.numOfPackages) : null,
          NumOfPallets: pListData.numOfPallets ? parseFloat(pListData.numOfPallets) : null,
          CarrierName: pListData.carrierName || null,
          DeliveryMethodNo: pListData.deliveryMethodNo,
          PackZoneID: pListData.packZoneID || null,
          PackageRemark: pListData.packageRemark || null
        };

        console.log('Updating PList with data:', updatedPList);
        await pickingService.updatePickingList(pListId, updatedPList);
        console.log('PList updated successfully');
      }

      // Update PListOrders with delivery info
      for (const [orderNo, orderData] of Array.from(pListOrdersData.entries())) {
        const updateRequest = {
          totalVolume: orderData.totalVolume ? parseFloat(orderData.totalVolume) : null,
          totalWeight: orderData.totalWeight ? parseFloat(orderData.totalWeight) : null,
          numOfPackages: orderData.numOfPackages ? parseInt(orderData.numOfPackages) : null,
          numOfPallets: orderData.numOfPallets ? parseFloat(orderData.numOfPallets) : null,
          carrierName: orderData.carrierName || null,
          deliveryMethodNo: orderData.deliveryMethodNo,
          packZoneID: orderData.packZoneID || null,
          packageRemark: orderData.packageRemark || null
        };

        await plistOrderService.update(
          pickingDetails!.header.companyNo,
          pListId,
          orderNo,
          updateRequest
        );
      }

      // Create consolidation Shipment if requested (samlasting)
      if (consolidate && pListOrders.length > 1) {
        console.log('Creating consolidation shipment for samlasting...');
        const firstOrder = pListOrders[0];
        const consolidationResponse = await packingService.createConsolidationShipment({
          pListID: pListId,
          orderNos: pListOrders.map(o => o.orderNo),
          customerNo: firstOrder.customerNo,
          delCustomerName: firstOrder.delName,
          delAddress1: firstOrder.delAddress1,
          delAddress2: firstOrder.delAddress2,
          delZipCode: firstOrder.delPostNo,
          delCity: firstOrder.delCity,
          delCountryCode: firstOrder.delCountry
        });
        console.log('Consolidation shipment created:', consolidationResponse);
      }

      // Save ShipmentLines if provided (kolli-dimensjoner for TA-integrasjon)
      if (shipmentLinesData && shipmentLinesData.size > 0) {
        console.log('Saving shipment lines for orders...');
        for (const [orderNo, lines] of Array.from(shipmentLinesData.entries())) {
          if (lines.length > 0) {
            await shipmentLineService.saveAll({
              pListID: pListId,
              orderNo: orderNo,
              lines: lines.map((line, index) => ({
                lineID: line.lineID || undefined,
                transitPackagingID: line.transitPackagingID,
                description1: line.description1,
                numOfPackages: line.numOfPackages,
                weight: line.weight,
                volume: line.volume,
                height: line.height,
                width: line.width,
                length: line.length,
                sorting: line.sorting ?? index
              }))
            });
            console.log(`Saved ${lines.length} shipment lines for order ${orderNo}`);
          }
        }
      }

      // TA-forhåndsvalidering (36369121): kjøres ETTER at leveringsinfo (vekt/kolli) er
      // lagret — payloaden bygges fra DB og blir identisk med bookingens. Gated bak
      // ta.validateBeforeCompletion (backend svarer applicable=false når av, så kallet
      // er ett raskt oppslag for alle andre). Fail-open: nettverksfeil her skal aldri
      // blokkere avslutningen.
      try {
        const validation = await pickingService.validateTaBeforeCompletion(pListId);
        if (validation.applicable && !validation.valid) {
          setTaValidationIssues(validation.issues || []);
          setShowTaValidation(true);
          return; // flyten fortsetter i handleTaValidationProceed / -Cancel
        }
      } catch (validationError) {
        console.warn('TA-validering utilgjengelig — fortsetter uten (fail open):', validationError);
      }

      // Complete picking after delivery info is saved.
      // Lastbærer-gate (36334239): backend avviser med CARRIER_REQUIRED når settingen er på
      // og ingen kollilinje har lastbærer. Da åpnes gate-dialogen, der operatøren kan velge
      // å registrere lastbærere (fordelingsbildet) eller avslutte uten (stemples sporbart).
      await completePickingAndFinish();
    } catch (error) {
      console.error('Error saving delivery info and completing picking:', error);
      throw error;
    }
  }, [pListId, pickingDetails, pListOrders, completePickingAndFinish]);

  // ── Leveringsmåte-varsel ved avslutt plukk (Strømmes) ──────────────────────
  // Sjekken kjøres på de ENDELIG VALGTE leveringsmåtene i leveranseinformasjonen
  // («Transportør»-feltet = DeliveryMethods) — ikke bare det som lå på ordrene fra
  // før. Dermed fanges både ERP-importerte ordrer med leveringsmåte og tilfeller
  // der operatøren velger/endrer leveringsmåte i avslutningsbildet.
  const pendingDeliveryConfirmRef = useRef<{
    pListData: DeliveryInfoPListData;
    pListOrdersData: Map<number, DeliveryInfoPListData>;
    shipmentLinesData?: Map<number, ShipmentLineRow[]>;
    consolidate?: boolean;
  } | null>(null);

  const handleDeliveryInfoConfirm = useCallback(async (
    pListData: DeliveryInfoPListData,
    pListOrdersData: Map<number, DeliveryInfoPListData>,
    shipmentLinesData?: Map<number, ShipmentLineRow[]>,
    consolidate?: boolean
  ) => {
    // Fail-open: feil i varsel-oppslaget skal aldri blokkere avslutningen.
    try {
      // (orderNo, deliveryMethodNo) for alle ordrer — per-ordre-verdier når de
      // finnes, ellers plukklistens felles valg (samlasting/kopier identisk).
      const pairs: Array<{ orderNo: number; methodNo: number }> = [];
      if (pListOrdersData.size > 0) {
        Array.from(pListOrdersData.entries()).forEach(([orderNo, data]) => {
          const methodNo = data.deliveryMethodNo ?? pListData.deliveryMethodNo;
          if (methodNo != null) pairs.push({ orderNo, methodNo });
        });
      } else if (pListData.deliveryMethodNo != null) {
        const orderNos = pListOrders.length > 0 ? pListOrders.map(o => o.orderNo) : [0];
        orderNos.forEach(orderNo => pairs.push({ orderNo, methodNo: pListData.deliveryMethodNo! }));
      }

      if (pairs.length > 0) {
        const allMethods = await deliveryMethodService.getAll();
        const warnings: PickCompletionWarning[] = [];
        pairs.forEach(({ orderNo, methodNo }) => {
          const method = allMethods.find(m => m.deliveryMethodNo === methodNo);
          if (method?.pickCompletionWarningYesNo) {
            warnings.push({
              orderNo,
              deliveryMethodNo: method.deliveryMethodNo,
              deliveryMethodDescription: method.description,
              warningText: method.pickCompletionWarningText
            });
          }
        });
        if (warnings.length > 0) {
          pendingDeliveryConfirmRef.current = { pListData, pListOrdersData, shipmentLinesData, consolidate };
          setPickCompletionWarnings(warnings);
          setShowPickCompletionWarning(true);
          return; // fortsetter i handlePickCompletionWarningConfirm
        }
      }
    } catch (error) {
      console.error('Error checking delivery method pick warnings (continuing):', error);
    }

    await executeDeliveryInfoConfirm(pListData, pListOrdersData, shipmentLinesData, consolidate);
  }, [pListOrders, executeDeliveryInfoConfirm]);

  /** OK i leveringsmåte-varselet — kjør fullføringen som ble holdt tilbake */
  const handlePickCompletionWarningConfirm = useCallback(async () => {
    setShowPickCompletionWarning(false);
    const pending = pendingDeliveryConfirmRef.current;
    pendingDeliveryConfirmRef.current = null;
    if (!pending) return;
    try {
      await executeDeliveryInfoConfirm(
        pending.pListData, pending.pListOrdersData, pending.shipmentLinesData, pending.consolidate);
    } catch (error) {
      // Kjøres utenfor DeliveryInfoModal sin catch — vis samme generiske melding
      console.error('Error completing picking after warning confirm:', error);
      alert(t('warehouse.modules.picking.delivery.saveFailed', {},
        'Kunne ikke lagre leveranseinformasjon. Prøv igjen.'));
    }
  }, [executeDeliveryInfoConfirm, t]);

  /** Avbryt i leveringsmåte-varselet — tilbake til leveranseinformasjonen */
  const handlePickCompletionWarningCancel = useCallback(() => {
    pendingDeliveryConfirmRef.current = null;
    setShowPickCompletionWarning(false);
  }, []);

  const handleQuantityDeviationConfirm = useCallback(() => {
    setShowQuantityDeviation(false);
    proceedToDeliveryInfo();
  }, [proceedToDeliveryInfo]);

  const handleQuantityDeviationCancel = useCallback(() => {
    setShowQuantityDeviation(false);
  }, []);

  const handleSignatureConfirm = useCallback(async (signatureDataUrl: string) => {
    try {
      if (!pickingDetails) return;

      const updatedPList = {
        ID: pickingDetails.header.id,
        CompanyNo: pickingDetails.header.companyNo,
        PListID: pickingDetails.header.pListID,
        Description1: pickingDetails.header.description1,
        Description2: pickingDetails.header.description2,
        PListDate: pickingDetails.header.pListDate,
        NumOfOrderLines: pickingDetails.header.numOfOrderLines,
        Weight: pickingDetails.header.weight,
        Priority: pickingDetails.header.priority,
        PListStatus: pickingDetails.header.pListStatus,
        OperatorID: pickingDetails.header.operatorID,
        LockedBy: pickingDetails.header.lockedBy,
        StartWorkDateTime: pickingDetails.header.startWorkDateTime,
        StopWorkDateTime: pickingDetails.header.stopWorkDateTime,
        DeliveryDate: pickingDetails.header.deliveryDate,
        Signature1: signatureDataUrl
      };

      console.log('Updating PList with signature');
      await pickingService.updatePickingList(pListId, updatedPList);
      console.log('Signature saved successfully');

      await loadPickingDetails();

      alert('Signatur lagret!');
    } catch (error) {
      console.error('Error saving signature:', error);
      throw error;
    }
  }, [pListId, pickingDetails, loadPickingDetails]);

  return {
    // Modal states
    showDeliveryInfoModal,
    setShowDeliveryInfoModal,
    showPackZoneSelection,
    setShowPackZoneSelection,
    showQuantityDeviation,
    quantityDeviations,
    showSignatureModal,
    setShowSignatureModal,

    // Leveringsmåte-varsel ved avslutt plukk (Strømmes)
    showPickCompletionWarning,
    pickCompletionWarnings,
    handlePickCompletionWarningConfirm,
    handlePickCompletionWarningCancel,

    // TA-forhåndsvalidering før fullføring (36369121)
    showTaValidation,
    taValidationIssues,
    taValidationBusy,
    handleTaValidationCancel,
    handleTaValidationProceed,

    // Lastbærer-gate + fordelingsbilde (36334239)
    showCarrierGate,
    showCarrierDistribution,
    carrierBusy,
    deliveryInfoKey,
    handleCompleteWithoutCarrier,
    handleOpenCarrierDistribution,
    handleCarrierDistributionConfirm,
    handleCarrierGateCancel,
    handleCarrierDistributionCancel,

    // Handlers
    handleCompletePicking,
    handleSendToPacking,
    handlePackZoneConfirm,
    handleDeliveryInfoConfirm,
    handleQuantityDeviationConfirm,
    handleQuantityDeviationCancel,
    handleSignatureConfirm,
  };
}
